import * as React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeading,
  Typography,
} from 'omakase';
import { Card, CardContent, LoadingContainer } from 'ui';
import { CheckoutOrigin } from 'state/selection';
import { GreyBlob } from 'icons';
import { IconCart, IconInfoCircle } from '@tock/icons';
import { IconEmptyCart, IconInformation } from 'ui/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { consumer, core } from '@tock/coretypes';
import { groupOptionOrders, groupOptionOrdersByParentGroupId } from '@tock/shared/utils/option';
import { isPrimaryPatron } from 'utils/purchase';
import { isThirdPartyDeliveryVariety } from '@tock/shared/utils/offerings/varietyQueries';
import { makeStyles } from '@material-ui/core/styles';
import { offeringPartyCollectiveItem } from '@tock/shared/utils/offerings';
import ConfirmOptionsDialogActions from './ConfirmOptionsDialogActions';
import GroupedOption from './GroupedOption';
import ItemizedReceipt from 'components/business/receipts/ItemizedReceipt';
import _ from 'lodash';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  alert: {
    marginBottom: theme.spacing(3),
  },
  dialogContent: {
    backgroundColor: theme.colors.bgLight,
    paddingBottom: 0,
    paddingTop: `${theme.spacing(2)}px !important`,
  },
  itemPrice: {
    alignItems: 'baseline',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
  },
  itemName: {
    flex: '0 0 60%',
    textAlign: 'left',
  },
  footer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  },
  headerRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  headerRowMBLarge: {
    marginBottom: theme.spacing(3),
  },
  headerRowMBSmall: {
    marginBottom: theme.spacing(1),
  },
  counterActive: {
    color: theme.colors.fgPrimaryBase,
  },
  counterDisabled: {
    color: theme.colors.fgDisabled,
  },
  section: {
    backgroundColor: theme.colors.bgDefault,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    '&:last-child': {
      marginBottom: 0,
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3.5),
    },
  },
  emptyStateRoot: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    '&:last-child': {
      paddingBottom: theme.spacing(4),
    },
  },
  emptyIconContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  emptyIcon: {
    fontSize: theme.typography.pxToRem(32),
    position: 'absolute',
    top: '50%',
    left: '50%',
    margin: '-1.6rem 0 0 -1.6rem',
  },
  blob: {
    color: theme.colors.bgLight,
    width: theme.typography.pxToRem(51),
    height: theme.typography.pxToRem(39),
  },
  actions: {
    flexDirection: 'column-reverse',
    '& button': {
      marginTop: theme.spacing(1),
      marginLeft: 0,
      '&:last-child': {
        marginLeft: 0,
        marginTop: 0,
      },
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      '& button': {
        marginRight: theme.spacing(1),
        marginTop: 0,
        '&:last-child': {
          marginTop: 0,
          marginRight: 0,
        },
      },
    },
  },
}));

interface ModalProps {
  isOpen: boolean;
  onClose(ticketOptionGroupId?: number): void;
  onComplete(): void;
  onSubmitOrder(): void;
  confirmDisabled: boolean;
  submitting: boolean;
}

interface Props {
  business: consumer.IBusinessConfig;
  purchase: core.PurchaseOrder;
  patron?: core.IPatron;
  ticketType: core.ITicketType;
  optionOrders: core.IOptionOrder[];
  partyOptionOrders?: core.IOptionOrder[];
  topLevelTicketOptionGroups: core.ITicketOptionGroup[];
  ticketOptions: core.ITicketOption[];
  ticketOptionPrices: core.IPrice[];
  extraChargeDetails: core.ExtraChargeDetails;
  onEditOptionOrder(optionOrder: core.IOptionOrder[]): void;
  onRemoveOptionOrder(optionOrder: core.IOptionOrder[]): void;
  checkoutOrigin?: CheckoutOrigin;
  /**
   * Handles whether to show the receipt and price information on the selected items.
   */
  showPriceInformation?: boolean;
  /**
   * Handles whether selected items (`optionOrders`) should be shown grouped together with a header
   */
  showSelectionsBySection?: boolean;
}

const ConfirmOptionsDialog: React.FunctionComponent<Props & ModalProps> = ({
  business,
  purchase,
  ticketType,
  optionOrders,
  partyOptionOrders,
  extraChargeDetails,
  ticketOptions,
  ticketOptionPrices,
  topLevelTicketOptionGroups,
  onEditOptionOrder,
  onRemoveOptionOrder,
  isOpen,
  onClose,
  onComplete,
  onSubmitOrder,
  confirmDisabled,
  submitting,
  patron,
  checkoutOrigin,
  showPriceInformation = true,
  showSelectionsBySection = false,
}) => {
  const classes = useStyles();
  // Have to wrap onClose or else optional parameters conflict
  const handleClose = () => onClose();

  const isOptionOrderInTicketOptions = (order: core.IOptionOrder) =>
    _.findIndex(ticketOptions, (ticketOption) => ticketOption.id === order.optionId) !== -1;

  const currentOrders = _.filter(
    optionOrders,
    (order) => !order.sentToKitchen && isOptionOrderInTicketOptions(order)
  );
  const itemCount = _.size(currentOrders);
  const groupedOptionOrdersBySectionId = groupOptionOrdersByParentGroupId(currentOrders);

  const partyGroupedOptionOrders = groupOptionOrders(
    _.filter(partyOptionOrders || [], (order) => isOptionOrderInTicketOptions(order))
  );
  const existingGroupedOptionOrders = groupOptionOrders(
    _.filter(optionOrders, (order) => !!order.sentToKitchen || !isOptionOrderInTicketOptions(order))
  );
  const isVisitEdit = !!purchase.visitEditToken;
  const type = offeringPartyCollectiveItem(ticketType.variety);

  return (
    <Dialog
      variant="partial"
      fullWidth
      aria-labelledby="confirm-dialog"
      open={isOpen}
      onClose={handleClose}
    >
      <DialogHeading id="confirm-dialog" onClose={handleClose}>
        {showPriceInformation ? `Confirm ${type}` : 'Your selections'}
      </DialogHeading>
      <DialogContent disablePadding className={classes.dialogContent}>
        <LoadingContainer loading={submitting} showChildrenWhileLoading={true}>
          <div className={classes.section}>
            {isVisitEdit ? (
              <div>
                <Typography variant="heading02" component="h1" color="fgDefault" mb={2}>
                  Please review and submit your {type}
                </Typography>
                <Typography variant="body02" component="div" mb={2}>
                  Once you submit your {type}, you won’t be able to remove those items. You can
                  continue to add new items until you pay your check.
                </Typography>
              </div>
            ) : (
              <div className={classes.headerRowMBLarge}>
                <div className={classes.headerRow}>
                  <Typography component="h1" variant="heading02" color="fgDefault">
                    {showPriceInformation ? `Your ${type}` : `${business.name ?? ''}`}
                  </Typography>
                  {!showSelectionsBySection && (
                    <Button variant="ghost" size="small" onClick={handleClose}>
                      Add items
                    </Button>
                  )}
                </div>
                <div className={classes.headerRow}>
                  {!showPriceInformation && ticketType.name && (
                    <Typography variant="body02" color="fgDefault">
                      {ticketType.name ?? ''}
                    </Typography>
                  )}
                </div>
              </div>
            )}
            {confirmDisabled ? (
              <Alert
                className={classes.alert}
                severity="warning"
                icon={
                  <IconMigrationUtil
                    legacyIcon={<IconInformation />}
                    newIcon={<IconInfoCircle />}
                  />
                }
              >
                To continue, please add items from required menu categories.
              </Alert>
            ) : null}
            {itemCount === 0 && purchase.subtotal === 0 && (
              <Card variant="outlined">
                <CardContent className={classes.emptyStateRoot}>
                  <section className={classes.emptyIconContainer}>
                    <IconMigrationUtil
                      legacyIcon={<IconEmptyCart className={classes.emptyIcon} />}
                      newIcon={<IconCart className={classes.emptyIcon} />}
                    />
                    <GreyBlob className={classes.blob} />
                  </section>
                  <Typography component="h3" variant="heading03" mb={1}>
                    Your order is empty
                  </Typography>
                  <Typography variant="body02">
                    Add items to your order before continuing
                  </Typography>
                </CardContent>
              </Card>
            )}
            {(itemCount > 0 || purchase.subtotal > 0) && (
              <>
                {isVisitEdit && _.size(groupedOptionOrdersBySectionId) > 0 && (
                  <Typography
                    className={classes.itemName}
                    component="h5"
                    variant="body02Strong"
                    color="fgSubtle"
                    mb={1}
                  >
                    New items
                  </Typography>
                )}
                {_.map(
                  topLevelTicketOptionGroups,
                  ({ name: ticketOptionGroupName, id: ticketOptionGroupId }) => {
                    if (!ticketOptionGroupId) {
                      return null;
                    }
                    const optionOrdersInGroup =
                      groupedOptionOrdersBySectionId[ticketOptionGroupId] ?? [];

                    return (
                      <>
                        {(optionOrdersInGroup ?? []).length > 0 && !!ticketOptionGroupName && (
                          <div className={clsx(classes.headerRowMBSmall, classes.headerRow)}>
                            <Typography component="h5" variant="body02Strong" mb={1}>
                              {ticketOptionGroupName}
                            </Typography>
                            {showSelectionsBySection && (
                              <Button
                                variant="ghost"
                                size="medium"
                                onClick={() => onClose(ticketOptionGroupId)}
                              >
                                Add items
                              </Button>
                            )}
                          </div>
                        )}
                        {optionOrdersInGroup.map((entry) => (
                          <GroupedOption
                            key={entry.ticketOptionId}
                            entry={entry}
                            ticketOptionPrices={purchase.option}
                            onEditOptionOrder={onEditOptionOrder}
                            onRemoveOptionOrder={onRemoveOptionOrder}
                            showPriceInformation={showPriceInformation}
                          />
                        ))}
                      </>
                    );
                  }
                )}
                {_.size(existingGroupedOptionOrders) > 0 && (
                  <Typography
                    className={classes.itemName}
                    component="h5"
                    variant="body02Strong"
                    color="fgSubtle"
                    mb={1}
                  >
                    Ordered items
                  </Typography>
                )}
                {_.map(existingGroupedOptionOrders, (entry, key) => {
                  return (
                    <GroupedOption
                      key={key}
                      entry={entry}
                      ticketOptionPrices={purchase.option}
                      onEditOptionOrder={onEditOptionOrder}
                      onRemoveOptionOrder={onRemoveOptionOrder}
                      disabled
                      showPriceInformation={showPriceInformation}
                    />
                  );
                })}
                {isPrimaryPatron(purchase, patron) && (
                  <>
                    {_.size(partyGroupedOptionOrders) > 0 && (
                      <Typography
                        className={classes.itemName}
                        component="h5"
                        variant="body02Strong"
                        color="fgSubtle"
                        mb={1}
                      >
                        Party items
                      </Typography>
                    )}
                    {_.map(partyGroupedOptionOrders, (entry, key) => {
                      return (
                        <GroupedOption
                          key={key}
                          entry={entry}
                          ticketOptionPrices={purchase.option}
                          onEditOptionOrder={onEditOptionOrder}
                          onRemoveOptionOrder={onRemoveOptionOrder}
                          disabled
                        />
                      );
                    })}
                  </>
                )}
              </>
            )}
          </div>

          {showPriceInformation && isPrimaryPatron(purchase, patron) && (
            <div className={classes.section}>
              <ItemizedReceipt
                business={business}
                compAmount={purchase.compAmount}
                customCharge={purchase.customChargeOrder as core.CustomChargeOrder[]}
                deliveryServiceProvider={ticketType.deliveryServiceProvider}
                extraChargeDetails={extraChargeDetails as core.ExtraChargeDetails}
                options={{}}
                purchase={purchase}
                partySize={purchase.ticketCount}
                purchaseFees={purchase.fee}
                isVisitEdit={!!purchase.visitEditToken}
                isGratuityForThirdPartyDelivery={isThirdPartyDeliveryVariety(ticketType)}
                ticketType={ticketType}
              />
            </div>
          )}
        </LoadingContainer>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <ConfirmOptionsDialogActions
          business={business}
          ticketType={ticketType}
          purchase={purchase}
          onClose={onClose}
          isVisitEdit={isVisitEdit}
          patron={patron}
          onComplete={onComplete}
          onSubmitOrder={onSubmitOrder}
          confirmDisabled={confirmDisabled}
          submitting={submitting}
          itemCount={itemCount}
          partyOptionOrders={partyOptionOrders}
          optionsSentToKitchen={_.size(existingGroupedOptionOrders) > 0}
          checkoutOrigin={checkoutOrigin}
          allowOrderConfirmation={!showSelectionsBySection}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmOptionsDialog;
